import { motion } from "framer-motion";

const Section = ({ title, items, delayStart }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={{
        hidden: {},
        visible: {
          transition: {
            delayChildren: delayStart || 0,
            staggerChildren: 0.2,
          },
        },
      }}
      className="grid grid-cols-1 gap-4"
    >
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 },
        }}
        transition={{ duration: 0.5 }}
      >
        <h4 className="text-3xl">{title}</h4>
      </motion.div>

      {items.map((item, index) => (
        <motion.div
          key={index}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.5 }}
        >
          <a
            href={item.link}
            target="_blank"
            className="block group"
            rel="noreferrer"
          >
            <h3 className="text-md font-md">
              <span className="mr-2 text-xl">//</span>
              <span className="group-hover:underline">{item.title}</span>
            </h3>
            <p className="text-gray-600 text-sm">{item.description}</p>
          </a>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default function Portfolio() {
  const portfolioFinanceItems = [
    {
      title: "OranjeBTC",
      description:
        "OranjeBTC, backed by major investors including Winklevoss Capital and Ricardo Salinas, debuted on Brazil’s B3 exchange via a reverse merger, holding over 3,650 BTC and valued above $400 million. Now Latin America’s largest publicly traded Bitcoin treasury firm, OranjeBTC is expanding access to regulated Bitcoin investments and launching a financial education platform for Brazilians.",
      link: "https://www.oranjebtc.com/",
    }
  ];


  const portfolioTechItems = [
    {
      title: "Vest Markets",
      description:
        "Vest Markets is a 24/7 trading platform offering zero-fee access to equities, crypto, and FX with instant settlement and leverage up to 100x. Its flagship offering is a funded trader program. Backed by Portal Ventures, Coinbase, Jane Street, Selini, and Greylock, Vest is built by a team with roots in Optiver, AQR, Point72, Meta, and Robinhood.",
      link: "https://www.vestmarkets.com/",
    },
    {
      title: "Bound (bUSD)",
      description:
        "Bound is building the infrastructure to deliver the DeFi experience users expect while still being on Bitcoin layer 1. Led by their flagship product radFi, Bound will soon support all key DeFi primitives, from swaps and a launchpad to borrowing and lending against bitcoin. The future of DeFi is Bound to Bitcoin",
      link: "https://bound.money/",
    },
    {
      title: "Kind Designs",
      description:
        "Kind Designs, a Miami-based innovator, creates 3D-printed 'living seawalls' that are faster and more cost-effective than traditional methods, while supporting marine habitats. Using robotics and sustainable materials, they’re tackling sea level rise for coastal cities with backing from investors like Mark Cuban and Overlay Capital.",
      link: "https://www.kinddesigns.com/",
    },
  ];

  const portfolioFoodItems = [
    {
      title: "Wonder Food",
      description:
        "Wonder Group, founded by Marc Lore, is a food-tech pioneer with a $7 billion valuation, revolutionizing dining through multi-concept kitchens. With plans to expand from 46 to over 90 locations by 2025, they deliver top-tier dine-in, takeout, and delivery experiences.",
      link: "https://wonder.com",
    },
    {
      title: "Cob",
      description:
        "Cob is on a mission to free Americans from their corn dependency by creating insanely delicious, European-style snack foods from the ancient supergrain sorghum. Co-founded by Novak Djokovic, Cob’s products are naturally gluten-free, help support GLP-1 levels to keep you fuller longer, and are packed with antioxidants and prebiotic fiber. The lineup includes Tortilla Chips and Popcorn, with flavors like Olive Oil Pink Salt, Kettle Corn, White Cheddar, and Cacio e Pepe.",
      link: "https://cobfoods.com/",
    },
    {
      title: "Gelso and Grand (NYC)",
      description:
        "Gelso & Grand, nestled in NYC’s historic Little Italy at Mulberry and Grand, offers a modern take on Italian cuisine with house-made pastas and wood-fired pizzas. Known for its inviting indoor and outdoor dining spaces, this upscale-casual gem has earned a Travelers’ Choice award for excellence.",
      link: "https://gelsoandgrand.com",
    },
    {
      title: "Jajaja Mexicana (NYC and Miami)",
      description: "Jajaja Mexicana, a pioneering plant-based restaurant in New York City, redefines Mexican cuisine with an innovative, vegan-friendly approach. With multiple locations across the city, they blend authentic flavors with a commitment to sustainability, earning prestigious recognition like a Travelers' Choice award.",
      link: "https://jajajamexicana.com",
    },
    {
      title: "Jealous Fork Restaurant (Kendall, Florida)",
      description:
        "Jealous Fork, founded by Joaquin Ortiz and Henrik Telle in Miami, began as an acclaimed artisan pancake food truck before evolving into a beloved brick-and-mortar restaurant. Renowned for creative American cuisine and Instagram-worthy pancakes, they’ve earned “Best Food Truck in South Florida” honors and national attention on the Food Network.",
      link: "https://jealousfork.com",
    },
  ];

  return (
    <section className="bg-gray-100 py-40 px-6">
      <div className="max-w-4xl mx-auto grid lg:grid-cols-2 items-center lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8 lg:mb-0">
            <h2 className="text-6xl md:text-8xl font-bold mb-2">Portfolio</h2>
            <p className="text-gray-600">
              Our portfolio showcases a curated selection of innovative ventures
            </p>
          </div>
        </motion.div>

        <div className="pl-8">
          <Section
            title="Tech"
            items={portfolioTechItems}
            delayStart={0.2 * portfolioTechItems.length}
          />

          <motion.div
            className="border-t border-gray-400 my-8"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              delay: 0.5 * (1 + portfolioTechItems.length),
            }}
            style={{ transformOrigin: "left" }}
          ></motion.div>

          <Section title="Finance" items={portfolioFinanceItems} delayStart={0.5} />

          <motion.div
            className="border-t border-gray-400 my-8"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              delay: 0.5 * (1 + portfolioFinanceItems.length),
            }}
            style={{ transformOrigin: "left" }}
          ></motion.div>

          <Section title="Eats" items={portfolioFoodItems} delayStart={0.5} />

        </div>
      </div>
    </section>
  );
}
